import { useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Box, Button, Stack } from '@mui/material';
import { Building2, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { type GetUserSellerListingsQuery } from '@graphql/__generated__/graphql';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import {
  getLotStatusLabel,
  getLotStatusTagVariant,
} from '@modules/_shared/components/LotCardInfo';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { formatAddress } from '@modules/_shared/utils/formatting';
import {
  LeadCardRow,
  StatusDot,
  SubtitleTruncatedItem,
} from '@modules/users/user-overview/components/LeadCardRow';
import { useLocale } from '@shared/hooks/locale';

const GET_USER_SELLER_LISTINGS = gql(/* GraphQL */ `
  query GetUserSellerListings($userId: uuid!) {
    lots(
      where: { sellers: { user_id: { _eq: $userId } } }
      order_by: { created_at: desc }
      limit: 100
    ) {
      id
      created_at
      title
      status
      property {
        route
        street_number
        postcode
        locality
        state
        country
      }
    }
    lots_aggregate(where: { sellers: { user_id: { _eq: $userId } } }) {
      aggregate {
        count
      }
    }
  }
`);

type Listing = NonNullable<GetUserSellerListingsQuery['lots'][number]>;

const LOT_STATUS_DOT_COLOR: Record<string, string> = {
  info: 'info.light',
  warning: 'warning.light',
  success: 'success.light',
  error: 'error.light',
  neutral: 'grey.400',
};

const ListingListItem = ({ listing }: { listing: Listing }) => {
  const { t } = useLocale();

  const address = listing.property
    ? formatAddress(listing.property, ', ')
    : null;
  const listingTitle = listing.title?.trim();
  const statusLabel = getLotStatusLabel(listing.status, t);
  const statusVariant = getLotStatusTagVariant(listing.status);
  const statusDotColor = LOT_STATUS_DOT_COLOR[statusVariant] ?? 'grey.400';

  return (
    <LeadCardRow
      icon={<Building2 size={18} />}
      color="secondary"
      to={`/listings/${listing.id}`}
      external
      date={listing.created_at}
      title={address || listingTitle || '-'}
      subtitle={
        <>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              flexShrink: 0,
            }}
          >
            <StatusDot color={statusDotColor} />
            {statusLabel}
          </Box>
          {listingTitle && address && (
            <SubtitleTruncatedItem>{listingTitle}</SubtitleTruncatedItem>
          )}
        </>
      }
    />
  );
};

type UserListingsCardProps = {
  userId: string;
  defaultExpanded?: boolean;
};

export const UserListingsCard = ({
  userId,
  defaultExpanded = false,
}: UserListingsCardProps) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);

  const { data, error } = useQuery(GET_USER_SELLER_LISTINGS, {
    variables: { userId },
  });

  useEffect(() => {
    if (error) {
      setApolloError(error);
    }
  }, [error]);

  const listings = data?.lots ?? [];
  const count = data?.lots_aggregate?.aggregate?.count ?? 0;

  return (
    <>
      <ListingAccordion
        title={
          <>
            {t('Listings')}
            {count > 0 && <CountBadge count={count} />}
          </>
        }
        defaultExpanded={defaultExpanded}
      >
        <Stack sx={{ gap: 0.5 }}>
          {listings.map(listing => (
            <ListingListItem key={listing.id} listing={listing} />
          ))}
          <Button
            fullWidth
            variant="soft"
            color="primary"
            startIcon={<Plus />}
            onClick={() =>
              navigate({
                pathname: '/listings/new/scratch',
                search: `defaultSellerId=${userId}`,
              })
            }
            sx={{ mt: listings.length > 0 ? 1 : 0 }}
          >
            {t('Create listing')}
          </Button>
        </Stack>
      </ListingAccordion>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />
    </>
  );
};
